// WP-3.3 (M3 PJ-1..7, S-24 `/projects/:id`): header, "Agreed X (was range Y-Z) -> FINAL W"
// line, the PJ-3 value-adjustment ledger, the four MI-3 lenses as a real segmented control
// (WP-3.6), an hours table + log-hours form for hourly projects, and PJ-7's relink affordance
// for unallocated income. Schedule closed in WP-3.4, Costs/Margin in WP-3.6, Timeline in
// WP-3.7 (every gap this WP's own predecessors flagged in their own CHANGELOG entries, now
// closed) — S-24's own Payments-allocated/Files sections are the only remaining trims
// (payments themselves already land via the Schedule/Unallocated-income sections; Files has
// no owning WP anywhere in DEV_PLAN yet).
import {
  formatMoney,
  type ConfidenceLevel,
  type CurrencyCode,
  type ProjectAdjustmentType,
  type ProjectStatus,
  type RevenueBasis,
} from '@pfos/engine';
import type { ProjectAdjustment, ProjectHoursEntry } from '@pfos/shared';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { EntityTagSection } from '../components/EntityTagSection';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { NotesSection } from '../components/NotesSection';
import { TimelineFeed } from '../components/TimelineFeed';
import {
  useAddProjectAdjustment,
  useEditProject,
  useLogProjectHours,
  useProject,
  useProjectProfitability,
  useProjectValueLenses,
} from '../lib/projects';
import { useCreateScheduleItem, useScheduleItems } from '../lib/scheduleItems';
import { useRelinkTransactionToProject, useTransactions } from '../lib/transactions';

const STATUS_OPTIONS: ProjectStatus[] = ['active', 'paused', 'completed', 'cancelled', 'archived'];

const ADJUSTMENT_TYPE_OPTIONS: { value: ProjectAdjustmentType; label: string; signHint: string }[] =
  [
    { value: 'extra_work', label: 'Extra work', signHint: 'usually positive' },
    { value: 'discount', label: 'Discount', signHint: 'usually negative — note required' },
    { value: 'compensation', label: 'Compensation', signHint: 'note required' },
    { value: 'scope_reduction', label: 'Scope reduction', signHint: 'usually negative' },
    {
      value: 'hourly_accrual',
      label: 'Hourly accrual',
      signHint: 'logged via hours, not entered directly',
    },
    { value: 'correction', label: 'Correction', signHint: 'either sign' },
  ];

const NOTE_REQUIRED_TYPES: ProjectAdjustmentType[] = ['discount', 'compensation'];

function AdjustmentLedger({
  currency,
  adjustments,
}: {
  currency: CurrencyCode;
  adjustments: ProjectAdjustment[];
}): React.JSX.Element {
  if (adjustments.length === 0)
    return (
      <p className="text-sm text-muted">
        No adjustments yet — Final Value equals the agreed amount.
      </p>
    );
  return (
    <ul className="divide-y divide-border">
      {adjustments.map((a) => (
        <li key={a.id} className="flex items-center justify-between py-2 text-sm">
          <div>
            <p className="text-ink capitalize">{a.type.replace('_', ' ')}</p>
            <p className="text-xs text-muted">
              {new Date(a.date).toLocaleDateString()}
              {a.note !== undefined ? ` — ${a.note}` : ''}
            </p>
          </div>
          <Amount
            minorUnits={a.amountMinor}
            currency={currency}
            className={a.amountMinor < 0 ? 'text-danger' : 'text-ink'}
          />
        </li>
      ))}
    </ul>
  );
}

function AddAdjustmentForm({
  projectId,
  currency,
}: {
  projectId: string;
  currency: CurrencyCode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ProjectAdjustmentType>('extra_work');
  const [amount, setAmount] = useState('');
  const [negative, setNegative] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const addMutation = useAddProjectAdjustment(projectId);
  const noteRequired = NOTE_REQUIRED_TYPES.includes(type);

  if (!open) {
    return (
      <button
        type="button"
        className={ghostButtonInlineClass}
        onClick={() => {
          setOpen(true);
        }}
      >
        Add adjustment
      </button>
    );
  }

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      <Field label="Type" htmlFor="adj-type">
        <select
          id="adj-type"
          className={selectClass}
          value={type}
          onChange={(e) => {
            setType(e.target.value as ProjectAdjustmentType);
          }}
        >
          {ADJUSTMENT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label} ({o.signHint})
            </option>
          ))}
        </select>
      </Field>
      <div className="mb-3 flex items-end gap-3">
        <div className="flex-1">
          <Field label="Amount" htmlFor="adj-amount">
            <AmountField id="adj-amount" currency={currency} value={amount} onChange={setAmount} />
          </Field>
        </div>
        <label className="mb-3 flex items-center gap-1 text-sm text-muted">
          <input
            type="checkbox"
            checked={negative}
            onChange={(e) => {
              setNegative(e.target.checked);
            }}
          />
          Reduces value
        </label>
      </div>
      <Field label="Date" htmlFor="adj-date">
        <input
          id="adj-date"
          type="date"
          className={inputClass}
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
      </Field>
      <Field label={noteRequired ? 'Note (required)' : 'Note (optional)'} htmlFor="adj-note">
        <input
          id="adj-note"
          className={inputClass}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </Field>
      <div className="flex gap-2">
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={addMutation.isPending}
          onClick={() => {
            setError(null);
            const magnitude = Math.round(Number(amount) * 100);
            if (!(magnitude > 0)) {
              setError('Amount must be a positive number');
              return;
            }
            if (noteRequired && note.trim() === '') {
              setError('A note is required for this adjustment type');
              return;
            }
            addMutation.mutate(
              {
                type,
                amountMinor: negative ? -magnitude : magnitude,
                date: new Date(date).toISOString(),
                ...(note.trim() !== '' ? { note: note.trim() } : {}),
              },
              {
                onSuccess: () => {
                  setOpen(false);
                  setAmount('');
                  setNote('');
                  setNegative(false);
                },
                onError: (e) => {
                  setError(e.message);
                },
              },
            );
          }}
        >
          Save adjustment
        </button>
        <button
          type="button"
          className={ghostButtonInlineClass}
          onClick={() => {
            setOpen(false);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function HoursSection({
  projectId,
  currency,
  hoursEntries,
}: {
  projectId: string;
  currency: CurrencyCode;
  hoursEntries: ProjectHoursEntry[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const logMutation = useLogProjectHours(projectId);

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-ink">Hours logged (PJ-5)</p>
      {hoursEntries.length === 0 ? (
        <p className="text-sm text-muted">No hours logged yet.</p>
      ) : (
        <ul className="mb-3 divide-y divide-border">
          {hoursEntries.map((h) => (
            <li key={h.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <p className="text-ink">
                  {h.hours}h @ {formatMoney({ currency, minorUnits: h.rateMinor })}/h
                </p>
                <p className="text-xs text-muted">
                  {new Date(h.date).toLocaleDateString()}
                  {h.note !== undefined ? ` — ${h.note}` : ''}
                </p>
              </div>
              <Amount minorUnits={Math.round(h.hours * h.rateMinor)} currency={currency} />
            </li>
          ))}
        </ul>
      )}
      {!open ? (
        <button
          type="button"
          className={ghostButtonInlineClass}
          onClick={() => {
            setOpen(true);
          }}
        >
          Log hours
        </button>
      ) : (
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
          {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
          <Field label="Date" htmlFor="hours-date">
            <input
              id="hours-date"
              type="date"
              className={inputClass}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
              }}
            />
          </Field>
          <div className="flex gap-3">
            <Field label="Hours" htmlFor="hours-hours">
              <input
                id="hours-hours"
                className={inputClass}
                inputMode="decimal"
                value={hours}
                onChange={(e) => {
                  setHours(e.target.value);
                }}
              />
            </Field>
            <Field label="Rate" htmlFor="hours-rate">
              <AmountField id="hours-rate" currency={currency} value={rate} onChange={setRate} />
            </Field>
          </div>
          <Field label="Note (optional)" htmlFor="hours-note">
            <input
              id="hours-note"
              className={inputClass}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </Field>
          <div className="flex gap-2">
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={logMutation.isPending}
              onClick={() => {
                setError(null);
                const hoursValue = Number(hours);
                const rateMinor = Math.round(Number(rate) * 100);
                if (!(hoursValue > 0)) {
                  setError('Hours must be a positive number');
                  return;
                }
                if (!(rateMinor > 0)) {
                  setError('Rate must be a positive number');
                  return;
                }
                logMutation.mutate(
                  {
                    date: new Date(date).toISOString(),
                    hours: hoursValue,
                    rateMinor,
                    ...(note.trim() !== '' ? { note: note.trim() } : {}),
                  },
                  {
                    onSuccess: () => {
                      setOpen(false);
                      setHours('');
                      setRate('');
                      setNote('');
                    },
                    onError: (e) => {
                      setError(e.message);
                    },
                  },
                );
              }}
            >
              Save hours
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UnallocatedIncomeTab({
  projectId,
  currency,
}: {
  projectId: string;
  currency: CurrencyCode;
}): React.JSX.Element {
  const { data: transactions } = useTransactions({ type: 'income' });
  const unallocated = (transactions ?? []).filter(
    (t) => t.projectId === undefined && t.currency === currency && !t.reversed,
  );

  if (unallocated.length === 0)
    return <p className="text-sm text-muted">No unallocated income to relink (PJ-7).</p>;

  return (
    <ul className="divide-y divide-border">
      {unallocated.map((t) => (
        <RelinkRow
          key={t.id}
          transactionId={t.id}
          projectId={projectId}
          currency={t.currency}
          amountMinor={t.amountMinor}
          {...(t.note !== undefined ? { note: t.note } : {})}
        />
      ))}
    </ul>
  );
}

function RelinkRow({
  transactionId,
  projectId,
  currency,
  amountMinor,
  note,
}: {
  transactionId: string;
  projectId: string;
  currency: CurrencyCode;
  amountMinor: number;
  note?: string;
}): React.JSX.Element {
  const relinkMutation = useRelinkTransactionToProject(transactionId);
  return (
    <li className="flex items-center justify-between py-2 text-sm">
      <div>
        <p className="text-ink">{note ?? 'Income'}</p>
        <Amount minorUnits={amountMinor} currency={currency} />
      </div>
      <button
        type="button"
        className={ghostButtonInlineClass}
        disabled={relinkMutation.isPending}
        onClick={() => {
          relinkMutation.mutate(projectId);
        }}
      >
        Link to this project
      </button>
    </li>
  );
}

function NewScheduleItemInlineForm({
  projectId,
  party,
  partyId,
  currency,
  onDone,
}: {
  projectId: string;
  party: string;
  partyId: string | undefined;
  currency: CurrencyCode;
  onDone: () => void;
}): React.JSX.Element {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [milestone, setMilestone] = useState('');
  const [confidence, setConfidence] = useState<ConfidenceLevel>('confirmed');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateScheduleItem();

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg p-3">
      {error !== null && <p className="mb-2 text-sm text-danger">{error}</p>}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Expected amount" htmlFor="si-inline-amount">
          <AmountField
            id="si-inline-amount"
            currency={currency}
            value={amount}
            onChange={setAmount}
          />
        </Field>
        <Field label="Expected date (optional)" htmlFor="si-inline-date">
          <input
            id="si-inline-date"
            type="date"
            className={inputClass}
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
        </Field>
        <Field label="Milestone note" htmlFor="si-inline-milestone">
          <input
            id="si-inline-milestone"
            className={inputClass}
            value={milestone}
            onChange={(e) => {
              setMilestone(e.target.value);
            }}
          />
        </Field>
        <Field label="Confidence" htmlFor="si-inline-confidence">
          <select
            id="si-inline-confidence"
            className={selectClass}
            value={confidence}
            onChange={(e) => {
              setConfidence(e.target.value as ConfidenceLevel);
            }}
          >
            <option value="confirmed">Confirmed</option>
            <option value="likely">Likely</option>
            <option value="tentative">Tentative</option>
          </select>
        </Field>
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={createMutation.isPending}
          onClick={() => {
            setError(null);
            const expectedAmountMinor = Math.round(Number(amount) * 100);
            if (!(expectedAmountMinor > 0)) {
              setError('Amount must be a positive number');
              return;
            }
            createMutation.mutate(
              {
                party,
                ...(partyId !== undefined ? { partyId } : {}),
                projectId,
                currency,
                expectedAmountMinor,
                ...(date !== '' ? { expectedDate: new Date(date).toISOString() } : {}),
                ...(milestone.trim() !== '' ? { milestone: milestone.trim() } : {}),
                confidence,
              },
              {
                onSuccess: onDone,
                onError: (e) => {
                  setError(e.message);
                },
              },
            );
          }}
        >
          Save
        </button>
        <button type="button" className={ghostButtonInlineClass} onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ScheduleSection({
  projectId,
  party,
  partyId,
  currency,
}: {
  projectId: string;
  party: string;
  partyId: string | undefined;
  currency: CurrencyCode;
}): React.JSX.Element {
  const { data: items } = useScheduleItems({ projectId });
  const [adding, setAdding] = useState(false);

  return (
    <div>
      {items === undefined || items.length === 0 ? (
        <p className="mb-2 text-sm text-muted">No schedule items for this project yet (SC-1).</p>
      ) : (
        <ul className="mb-2 divide-y divide-border">
          {items.map((i) => (
            <li key={i.id} className="flex items-center justify-between py-2 text-sm">
              <Link to="/expected" className="text-primary hover:underline">
                {i.milestone ?? 'Schedule item'} ·{' '}
                <span className="capitalize">{i.status.replace('_', ' ')}</span>
              </Link>
              <span className="text-ink">
                {formatMoney({ currency: i.currency, minorUnits: i.remainingMinor })} remaining
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <NewScheduleItemInlineForm
          projectId={projectId}
          party={party}
          partyId={partyId}
          currency={currency}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : (
        <button
          type="button"
          className={ghostButtonInlineClass}
          onClick={() => {
            setAdding(true);
          }}
        >
          Add schedule item
        </button>
      )}
    </div>
  );
}

const LENS_OPTIONS: { label: string; key: 'agreed' | RevenueBasis }[] = [
  { label: 'Agreed Value', key: 'agreed' },
  { label: 'Final Project Value', key: 'final' },
  { label: 'Gross Received', key: 'gross' },
  { label: 'Net Usable', key: 'net' },
];

export function ProjectDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data: project, isLoading } = useProject(id);
  const { data: lenses } = useProjectValueLenses(id);
  const editMutation = useEditProject(id);
  // MI-3/S-24: the four lenses render as a segmented control on the money row — "Agreed
  // Value" is display-only (PP-2 excludes it as a profit basis: a pre-adjustment estimate,
  // never a real revenue figure), so selecting it just highlights the cell.
  const [selectedLens, setSelectedLens] = useState<'agreed' | RevenueBasis>('final');
  const profitBasis: RevenueBasis = selectedLens === 'agreed' ? 'final' : selectedLens;
  const { data: profitability } = useProjectProfitability(id, profitBasis);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (project === undefined) return <p className="text-sm text-muted">Project not found.</p>;

  const finalValueMinor = lenses?.finalValueMinor ?? project.agreedMinor;
  const rangeLabel =
    project.originalQuotedRange !== undefined
      ? ` (was range ${formatMoney({ currency: project.currency, minorUnits: project.originalQuotedRange.minMinor })}–${formatMoney({ currency: project.currency, minorUnits: project.originalQuotedRange.maxMinor })})`
      : '';

  return (
    <div>
      <Link
        to="/projects"
        className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={14} /> Projects
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{project.title}</h1>
          <p className="text-sm text-muted">
            {project.party} ·{' '}
            <span className="capitalize">{project.earningModel.replace('_', ' ')}</span>
          </p>
        </div>
        <select
          className={`${selectClass} w-auto`}
          aria-label="Project status"
          value={project.status}
          disabled={editMutation.isPending}
          onChange={(e) => {
            editMutation.mutate({ status: e.target.value as ProjectStatus });
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <p className="mb-4 text-lg text-ink">
        Agreed {formatMoney({ currency: project.currency, minorUnits: project.agreedMinor })}
        {rangeLabel} → FINAL{' '}
        {formatMoney({ currency: project.currency, minorUnits: finalValueMinor })}
      </p>

      {lenses !== undefined && (
        <div className="mb-4">
          <div
            className="mb-2 grid grid-cols-2 gap-3 sm:grid-cols-4"
            role="radiogroup"
            aria-label="Revenue lens"
          >
            {LENS_OPTIONS.map((lens) => {
              const value =
                lens.key === 'agreed'
                  ? lenses.agreedMinor
                  : lens.key === 'final'
                    ? lenses.finalValueMinor
                    : lens.key === 'gross'
                      ? lenses.grossReceivedMinor
                      : lenses.netUsableMinor;
              return (
                <button
                  key={lens.label}
                  type="button"
                  role="radio"
                  aria-checked={selectedLens === lens.key}
                  onClick={() => {
                    setSelectedLens(lens.key);
                  }}
                  className={`rounded-[var(--radius-card)] border p-3 text-left ${
                    selectedLens === lens.key
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-surface'
                  }`}
                >
                  <p className="text-xs text-muted">{lens.label}</p>
                  <p className="text-sm font-medium text-ink">
                    <Amount minorUnits={value} currency={project.currency} />
                  </p>
                </button>
              );
            })}
          </div>
          {profitability !== undefined && (
            <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
              <p className="text-xs text-muted">
                Costs (
                {profitBasis === 'final'
                  ? 'vs Final'
                  : profitBasis === 'gross'
                    ? 'vs Gross'
                    : 'vs Net'}
                )
              </p>
              <p className="text-sm font-medium text-ink">
                <Amount minorUnits={profitability.directCostsMinor} currency={project.currency} />
                {profitability.marginPercent !== null && (
                  <span
                    className={profitability.marginPercent >= 0 ? 'text-success' : 'text-danger'}
                  >
                    {' '}
                    · M {Math.round(profitability.marginPercent * 100)}%
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-2 text-sm font-medium text-ink">
          Value ledger (PJ-3 — LOCKED, original agreement never changes)
        </p>
        <AdjustmentLedger currency={project.currency} adjustments={project.adjustments} />
        <div className="mt-3">
          <AddAdjustmentForm projectId={id} currency={project.currency} />
        </div>
      </div>

      {profitability !== undefined && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="mb-2 text-sm font-medium text-ink">
            Costs ({profitability.costs.length}) — PP-1, linked expenses
          </p>
          {profitability.costs.length === 0 ? (
            <p className="text-sm text-muted">No costs linked to this project yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {profitability.costs.map((c) => (
                <li
                  key={c.transactionId}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <Link to={`/tx/${c.transactionId}`} className="text-primary hover:underline">
                    {c.category ?? 'Expense'} · {new Date(c.at).toLocaleDateString()}
                  </Link>
                  <Amount minorUnits={c.amountMinor} currency={project.currency} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {project.earningModel === 'hourly' && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <HoursSection
            projectId={id}
            currency={project.currency}
            hoursEntries={project.hoursEntries}
          />
        </div>
      )}

      <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-2 text-sm font-medium text-ink">
          Schedule (SC-1..8 — full list/rollups/aging at{' '}
          <Link to="/expected" className="text-primary hover:underline">
            Expected
          </Link>
          )
        </p>
        <ScheduleSection
          projectId={id}
          party={project.party}
          partyId={project.partyId}
          currency={project.currency}
        />
      </div>

      <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-2 text-sm font-medium text-ink">Unallocated income (PJ-7)</p>
        <UnallocatedIncomeTab projectId={id} currency={project.currency} />
      </div>

      <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-2 text-sm font-medium text-ink">Timeline (TL-1)</p>
        <TimelineFeed filters={{ projectId: id }} showTypeFilter={false} />
      </div>

      {project.notes !== undefined && (
        <p className="mb-4 text-sm text-muted">Notes: {project.notes}</p>
      )}

      <div className="mb-4">
        <EntityTagSection entityType="project" entityId={id} />
      </div>

      <NotesSection entityType="project" entityId={id} />
    </div>
  );
}
